import { useState, useEffect } from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Users,
  ChevronDown,
  ChevronRight,
  Shield,
  Building2,
  Sparkles,
  Coins,
  FileText,
  Wand2,
} from 'lucide-react'

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'

const navigationItems = [
  {
    title: 'アカウント管理',
    icon: Users,
    items: [
      { title: 'ユーザー一覧', url: '/admins/users', icon: Users },
      { title: '代理店一覧', url: '/admins/agencies', icon: Building2 },
    ],
  },
  // {
  //   title: 'AI 機能',
  //   icon: Sparkles,
  //   items: [
  //     { title: 'AI シナリオ生成', url: '/admins/ai-scenario', icon: Wand2 },
  //     { title: 'AI コスト', url: '/admins/ai_costs', icon: Coins },
  //     { title: 'AI プロンプト管理', url: '/admins/ai_prompts', icon: FileText },
  //   ],
  // },
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

const SIDEBAR_STORAGE_KEY = 'admin-sidebar-collapsed-groups'

export function AdminSidebar() {
  const { state } = useSidebar()
  const { url } = usePage()
  const currentPath = url
  const collapsed = state === 'collapsed'

  // 閉じたグループだけを保存する（後から追加したグループは最初から開いた状態で出す）
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Failed to load sidebar state from localStorage:', error)
    }
    return []
  })

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(collapsedGroups))
    } catch (error) {
      console.error('Failed to save sidebar state to localStorage:', error)
    }
  }, [collapsedGroups])

  const toggleGroup = (groupTitle: string) => {
    setCollapsedGroups((prev) =>
      prev.includes(groupTitle)
        ? prev.filter((title) => title !== groupTitle)
        : [...prev, groupTitle]
    )
  }

  const isGroupExpanded = (groupTitle: string) =>
    !collapsedGroups.includes(groupTitle)
  const isActive = (path: string) => currentPath === path || currentPath.startsWith(path + '/')

  const getNavClass = (active: boolean) => {
    return classNames(
      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold transition-colors',
      active
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    )
  }

  return (
    <Sidebar className="border-r border-border bg-sidebar" collapsible="icon">
      <SidebarContent className="gap-0">
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center border-b border-border px-6">
          <Link href="/admins/users" className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {!collapsed && (
              <div className="text-xl font-bold text-primary">管理者パネル</div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-4">
          {navigationItems.map((group) => {
            const isExpanded = isGroupExpanded(group.title)
            const hasActiveItem = group.items.some((item) => isActive(item.url))

            return (
              <SidebarGroup key={group.title} className="mb-4">
                <SidebarGroupLabel
                  onClick={() => toggleGroup(group.title)}
                  className={classNames(
                    'flex cursor-pointer items-center justify-between text-xs font-semibold uppercase tracking-wider transition-colors',
                    hasActiveItem
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <group.icon className="h-4 w-4" />
                    {!collapsed && <span>{group.title}</span>}
                  </div>
                  {!collapsed && (
                    <div className="ml-auto">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </SidebarGroupLabel>

                {(isExpanded || collapsed) && (
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {group.items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild>
                            <Link
                              href={item.url}
                              className={getNavClass(isActive(item.url))}
                            >
                              <item.icon className="h-4 w-4 shrink-0" />
                              {!collapsed && <span>{item.title}</span>}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                )}
              </SidebarGroup>
            )
          })}
        </div>
      </SidebarContent>
    </Sidebar>
  )
}
